import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Popconfirm,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'
import { blue, redPrimary } from '../../color'
import { ASSIGNEE_TYPE, PROPOSAL_STATUS } from '../../constants'
import date_format from '../../date_format'
import { AvatarHasName } from '../Common/Avatar'
import SelectPeoplePopup from '../SelectPeoplePopup'

const { Step } = Steps

// Styled component for ProposalStepApproveUpdate
const StepsWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
  position: relative;
  .steps {
    width: 100% !important;
    display: ${({ count, i }) => (count > i ? '-webkit-box' : '')};
    transform: translateX(30px);
    .ant-steps-item-title,
    .ant-steps-item-description {
      color: #000000d9 !important;
    }
    .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
      color: #000000d9 !important;
      font-weight: 500;
    }
    .ant-steps-item-content {
      width: ${({ i }) =>
        i == 4
          ? '225px'
          : i == 5
          ? '250px'
          : i == 6
          ? '275px'
          : i == 7
          ? '300px'
          : '350px'};
      transform: translateX(-30px);
    }
    .tag-selected {
      border-radius: 35px;
      padding: 2px 8px 2px 3px;
      display: flex;
      margin-right: 0;
      align-items: center;
    }
  }
  .ant-steps-label-vertical.ant-steps-small:not(.ant-steps-dot)
    .ant-steps-item-icon {
    margin-left: 70px !important;
  }
  .ant-steps-label-vertical .ant-steps-item-tail {
    margin-left: 86px;
    width: 90%;
  }
  @media screen and (min-width: 1600px) {
    .ant-steps-label-vertical.ant-steps-small:not(.ant-steps-dot)
      .ant-steps-item-icon {
      margin-left: 20px !important;
    }
    .ant-steps-label-vertical .ant-steps-item-tail {
      margin-left: 36px;
      width: 100%;
    }
  }
`

// Function to get user information
const getInfoUser = (username, departmentWithUserList) => {
  const defaultUser = {
    full_name: username || 'Unknown User',
    image_id: null,
    username: username || null,
    department: null,
  }

  if (!username || !departmentWithUserList) {
    return defaultUser
  }

  for (const department of departmentWithUserList) {
    if (!department?.users) continue
    const userTarget = department.users.find(
      user => user.user_name === username
    )
    if (userTarget) {
      return {
        full_name:
          userTarget.name_uppercase || userTarget.full_name || username,
        image_id: userTarget.image_id || null,
        username: username,
        department: department.name || null,
      }
    }
  }

  return defaultUser
}

// Function to render status
const renderStatus = (status, isRejected) => {
  const styleTag = {
    marginTop: 8,
    borderRadius: 10,
  }

  if (status === PROPOSAL_STATUS.PENDING) {
    return (
      <Tag
        color={isRejected ? null : 'geekblue'}
        style={{ ...styleTag, opacity: isRejected ? 0.5 : 1 }}>
        Đến lượt duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.WAITING) {
    return (
      <Tag
        color={isRejected ? null : 'orange'}
        style={{ ...styleTag, opacity: isRejected ? 0.5 : 1 }}>
        Chờ duyệt
      </Tag>
    )
  }

  if (status === PROPOSAL_STATUS.CREATED) {
    return (
      <Tag color={'cyan'} style={styleTag}>
        Đã tạo
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.APPROVED) {
    return (
      <Tag color={'green'} style={styleTag}>
        Đã duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.REJECTED) {
    return (
      <Tag color={'red'} style={styleTag}>
        Đã từ chối
      </Tag>
    )
  }
}

// Function to render time
const renderTime = level => {
  const styleTag = {
    marginTop: 4,
  }

  const textCreated = date_format.HH_mm_DD_MM_YYYY(level.created_at)
  const textUpdated = date_format.HH_mm_DD_MM_YYYY(level.updated_at)

  if (level.status === PROPOSAL_STATUS.CREATED) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textCreated}
      </p>
    )
  }
  if (
    level.status === PROPOSAL_STATUS.APPROVED ||
    level.status === PROPOSAL_STATUS.REJECTED
  ) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textUpdated}
      </p>
    )
  }

  return <></>
}

// List of assignee types
export const ASSIGNEE_TYPE_CONFIG = [
  ASSIGNEE_TYPE.CREATE,
  ASSIGNEE_TYPE.COMBINER,
  ASSIGNEE_TYPE.HANDLER,
  ASSIGNEE_TYPE.LEADER,
  ASSIGNEE_TYPE.UNIT_LEADER,
  ASSIGNEE_TYPE.HDQT,
  ASSIGNEE_TYPE.KT_KTNB,
  ASSIGNEE_TYPE.lanh_dao_vimc,
  ASSIGNEE_TYPE.ban_kt_ktnb,
]

const ProposalStepApproveUpdate = props => {
  const {
    proposalStore,
    selectPeopleStore,
    approvalLevelList,
    styleCre,
    onRemoveApprovalLevel,
    onUpdateApprovalLevels,
  } = props

  const { originDepartmentWithUserList, selectUserData } = selectPeopleStore
  const [currentStep, setCurrentStep] = useState(0)
  const [approvalLevels, setApprovalLevels] = useState([])
  const [isVisibleSelectUserPopup, setIsVisibleSelectUserPopup] = useState(
    false
  )
  const [statusStep, setStatusStep] = useState('process')

  useEffect(() => {
    const length = approvalLevelList.length
    if (approvalLevelList.length === 0) return
    let step = 0
    const pendingStep = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.PENDING
    )
    const endStep = approvalLevelList[length - 1]
    const stepRejected = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.REJECTED
    )
    if (pendingStep) {
      step = pendingStep.level
    }
    if (endStep.status === PROPOSAL_STATUS.APPROVED) {
      step = length
    }
    if (stepRejected) {
      step = stepRejected.levelselectUserData
      setStatusStep('error')
    } else {
      setStatusStep('')
    }
    setCurrentStep(step)
  }, [approvalLevelList])

  useEffect(() => {
    const approvalLevelsUpdate = approvalLevels.map(item => {
      if (!item.allow_edit) return item

      const assignTypeIndex = approvalLevels
        .filter(el => el.allow_edit)
        .findIndex(el => el.level === item.level)
      const assigneeType =
        assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
          ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
          : `DYNAMIC_LEVEL_${assignTypeIndex}`
      const selectedUser =
        selectUserData[assigneeType]?.length > 0
          ? selectUserData[assigneeType][0]
          : item.user || {}
      const userInfo = getInfoUser(
        selectedUser.id || item.username,
        originDepartmentWithUserList
      )

      return {
        ...item,
        username: selectedUser.id || item.username,
        user: {
          full_name: selectedUser.full_name || item.user?.full_name || '',
          image_id: selectedUser.image_id || item.user?.image_id || null,
          username: selectedUser.id || item.username,
          department: userInfo.department,
        },
      }
    })

    const hasChanges = approvalLevelsUpdate.some((newLevel, index) => {
      const oldLevel = approvalLevels[index]
      return (
        !oldLevel ||
        newLevel.username !== oldLevel.username ||
        newLevel.user?.full_name !== oldLevel.user?.full_name
      )
    })

    if (hasChanges) {
      setApprovalLevels(approvalLevelsUpdate)
      if (onUpdateApprovalLevels) onUpdateApprovalLevels(approvalLevelsUpdate)
      proposalStore.setDefaultApprovalConfigUpdate(approvalLevelsUpdate)
    }
  }, [
    selectUserData,
    approvalLevels,
    originDepartmentWithUserList,
    proposalStore,
    onUpdateApprovalLevels,
  ])

  useEffect(() => {
    proposalStore.setDefaultApprovalConfigUpdate(approvalLevels)
  }, [approvalLevels])

  useEffect(() => {
    const approvalLevelConvert = approvalLevelList.map(item => {
      return {
        ...item,
        user: getInfoUser(item.username, originDepartmentWithUserList),
      }
    })

    const levelAllowEditList = approvalLevelConvert.filter(
      item => item.allow_edit
    )

    const selectUserData = {}

    levelAllowEditList.forEach((item, index) => {
      if (!item.username || index >= ASSIGNEE_TYPE_CONFIG.length) return
      const user = getInfoUser(item.username, originDepartmentWithUserList)
      const assigneeType = ASSIGNEE_TYPE_CONFIG[index]
      selectUserData[assigneeType] = [
        {
          assignee_type: ASSIGNEE_TYPE.USER,
          full_name: user.full_name,
          id: user.username,
          image_id: user.image_id,
          permission: assigneeType,
        },
      ]
    })

    selectPeopleStore.setSelectUserData(selectUserData)

    setApprovalLevels(approvalLevelConvert)
  }, [approvalLevelList, originDepartmentWithUserList])

  const handleSubmitSelectUser = useCallback(() => {
    setIsVisibleSelectUserPopup(false)
  }, [])

  const handleOpenSelectUser = level => {
    let userType = null
    const levelAllowEditList = approvalLevelList.filter(item => item.allow_edit)
    const indexLevelAllowEdit = levelAllowEditList.findIndex(
      item => item.level === level
    )
    if (indexLevelAllowEdit !== -1) {
      userType = ASSIGNEE_TYPE_CONFIG[indexLevelAllowEdit]
    }
    if (userType) {
      selectPeopleStore.setAssigneePopupType(userType)
      setIsVisibleSelectUserPopup(true)
    } else {
      return message.warning('Không rõ chọn người loại nào!')
    }
  }

  const renderTitleStep = level => {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div>
          <span style={{ color: redPrimary }}>* </span>
          <span>{level?.user?.department || level?.name}</span>
        </div>
        {level.allow_edit && (
          <Fragment>
            <Tooltip title="Chọn người dùng" color={blue}>
              <Button
                icon={<UserOutlined style={{ color: blue }} />}
                onClick={() => handleOpenSelectUser(level.level)}
              />
            </Tooltip>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa cấp duyệt này?"
              okText="Xóa"
              cancelText="Không"
              onConfirm={() => {
                if (onRemoveApprovalLevel) {
                  onRemoveApprovalLevel(level)
                }
              }}>
              <Tooltip title="Xóa cấp duyệt" color="red">
                <Button icon={<DeleteOutlined style={{ color: 'red' }} />} />
              </Tooltip>
            </Popconfirm>
          </Fragment>
        )}
      </div>
    )
  }

  const renderDescStep = useCallback(
    level => {
      const assignTypeIndex = approvalLevels
        .filter(el => el.allow_edit)
        .findIndex(item => item.level === level.level)
      const assigneeType =
        assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
          ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
          : `DYNAMIC_LEVEL_${assignTypeIndex}`

      const handleDeleteUser = () => {
        if (level.allow_edit && level.username) {
          selectPeopleStore.handleRemoveSelect({
            assignee_type: ASSIGNEE_TYPE.USER,
            id: level.username,
            permission: assigneeType,
          })

          const updatedLevels = approvalLevels.map(item =>
            item.level === level.level
              ? { ...item, username: '', user: null }
              : item
          )
          setApprovalLevels(updatedLevels)

          if (onUpdateApprovalLevels) {
            onUpdateApprovalLevels(updatedLevels)
          }
        }
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: level.allow_edit ? 6 : 14,
          }}>
          {level.user?.username ? (
            <Tag
              className={'tag-selected'}
              color={'blue'}
              closable={level.allow_edit}
              onClose={handleDeleteUser}>
              <AvatarHasName
                imgId={level.user?.image_id}
                size={22}
                name={level.user?.full_name}
              />
            </Tag>
          ) : (
            <Tag style={{ marginTop: 6, borderRadius: 10 }}>
              Chưa gán người!
            </Tag>
          )}
          {renderStatus(level.status, statusStep === 'error')}
          {level.comment && level.comment !== '' && (
            <div style={{ marginTop: 4, fontStyle: 'italic' }}>
              <Typography.Paragraph
                ellipsis={{
                  rows: 15,
                  expandable: true,
                  onEllipsis: ellipsis => {
                    console.log('Ellipsis changed:', ellipsis)
                  },
                }}>
                <span style={{ fontWeight: 500, marginRight: 6 }}>Lý do:</span>
                {level.comment}
              </Typography.Paragraph>
            </div>
          )}
          {renderTime(level)}
        </div>
      )
    },
    [approvalLevels, selectPeopleStore, statusStep, onUpdateApprovalLevels]
  )

  return styleCre != null ? (
    // Ở trang create chỉ để 4 cấp duyệt ở 1 hàng
    <StepsWrapper count={approvalLevels.length} i={4}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => {
          return (
            <Step
              key={level.level}
              title={renderTitleStep(level)}
              description={renderDescStep(level)}
            />
          )
        })}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  ) : (
    // Ở trang create để 5 cấp duyệt ở 1 hàng
    <StepsWrapper count={approvalLevels.length} i={5}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => {
          return (
            <Step
              key={level.level}
              title={renderTitleStep(level)}
              description={renderDescStep(level)}
            />
          )
        })}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  )
}

ProposalStepApproveUpdate.propTypes = {}

export default memo(
  inject(
    'authenticationStore',
    'proposalStore',
    'selectPeopleStore'
  )(observer(ProposalStepApproveUpdate))
)
